import { useEffect, useState, type FormEvent } from "react";
import { useAuth } from "./auth";
import { ApiError, archiveAddressBookEntry, createAddressBookEntry, getCompanyLogoUrl, getCompanyProfile, discoverTrackingVehicles, getCompanyTracking, getFleet, listAddressBook, registerTrackingProviderInterest, removeCompanyLogo, saveCompanyProfile, saveCompanyTracking, disconnectCompanyTracking, saveTrackingVehicleMapping, updateAddressBookEntry, uploadCompanyLogo, type AddressBookEntry, type AddressBookInput, type BankDetails, type CompanyProfileInput, type CompanyTrackingConnection, type TrackingDiscoveredVehicle, type TrackingProviderKey, type TrackingSummary, type Vehicle } from "./api";

const blankAddress: AddressBookInput = {
  siteName: "",
  fullAddress: "",
  postcode: "",
};
const blankProfile: CompanyProfileInput = {
  tradingName: "",
  legalName: "",
  businessType: "company",
  registeredAddress: "",
  postcode: "",
  country: "United Kingdom",
  operationalTelephone: "",
  vatRegistered: false,
  paymentTermsDays: 30,
  bankDetails: {},
};

export function CompanySettingsPage() {
  const auth = useAuth();
  const company = auth.session!.companies[0]!;
  const owner = company.roles.includes("owner");
  const [profile, setProfile] = useState<CompanyProfileInput>(blankProfile);
  const [addresses, setAddresses] = useState<AddressBookEntry[]>([]);
  const [editing, setEditing] = useState<string>();
  const [address, setAddress] = useState<AddressBookInput>(blankAddress);
  const [logoUrl, setLogoUrl] = useState<string>();
  const [logoName, setLogoName] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  async function refresh() {
    const [p, a] = await Promise.all([getCompanyProfile(company.companyId), listAddressBook(company.companyId)]);
    setProfile({
      tradingName: p.tradingName,
      legalName: p.legalName,
      businessType: p.businessType,
      registeredAddress: p.registeredAddress,
      postcode: p.postcode,
      country: p.country,
      ...(p.operationalContactName ? { operationalContactName: p.operationalContactName } : {}),
      ...(p.operationalEmail ? { operationalEmail: p.operationalEmail } : {}),
      operationalTelephone: p.operationalTelephone,
      vatRegistered: p.vatRegistered,
      ...(p.vatNumber ? { vatNumber: p.vatNumber } : {}),
      paymentTermsDays: p.paymentTermsDays,
      ...(p.bankDetails ? { bankDetails: p.bankDetails } : owner ? { bankDetails: {} } : {}),
    });
    setAddresses(a);
    setLogoName(p.logo?.filename);
    if (p.logo) {
      void getCompanyLogoUrl(company.companyId)
        .then(setLogoUrl)
        .catch(() => setLogoUrl(undefined));
    } else setLogoUrl(undefined);
  }
  useEffect(() => {
    void refresh().catch((e) => setError(message(e)));
  }, [company.companyId]);

  function field<K extends keyof CompanyProfileInput>(key: K, value: CompanyProfileInput[K]) {
    setProfile((current) => ({ ...current, [key]: value }));
  }
  function bank(key: keyof BankDetails, value: string) {
    setProfile((current) => ({
      ...current,
      bankDetails: { ...current.bankDetails, [key]: value },
    }));
  }
  async function saveProfile(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await saveCompanyProfile(company.companyId, profile);
      await refresh();
      setNotice("Company settings saved.");
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }
  function editAddress(entry: AddressBookEntry) {
    setEditing(entry.id);
    setAddress({
      siteName: entry.siteName,
      fullAddress: entry.fullAddress,
      postcode: entry.postcode,
      ...(entry.contactName ? { contactName: entry.contactName } : {}),
      ...(entry.contactTelephone ? { contactTelephone: entry.contactTelephone } : {}),
      ...(entry.contactEmail ? { contactEmail: entry.contactEmail } : {}),
      ...(entry.accessInstructions ? { accessInstructions: entry.accessInstructions } : {}),
      ...(entry.openingHours ? { openingHours: entry.openingHours } : {}),
      ...(entry.notes ? { notes: entry.notes } : {}),
    });
  }
  function resetAddress() {
    setEditing(undefined);
    setAddress(blankAddress);
  }
  async function saveAddress(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (editing) await updateAddressBookEntry(company.companyId, editing, address);
      else await createAddressBookEntry(company.companyId, address);
      resetAddress();
      await refresh();
      setNotice("Address book saved.");
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }
  async function archive(entry: AddressBookEntry) {
    if (!window.confirm(`Remove ${entry.siteName} from the address book? Existing loads will not change.`)) return;
    setBusy(true);
    try {
      await archiveAddressBookEntry(company.companyId, entry.id);
      if (editing === entry.id) resetAddress();
      await refresh();
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }
  async function logo(file: File | undefined) {
    if (!file) return;
    setBusy(true);
    setError("");
    try {
      await uploadCompanyLogo(company.companyId, file);
      await refresh();
      setNotice("Company logo updated.");
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }
  async function removeLogo() {
    setBusy(true);
    try {
      await removeCompanyLogo(company.companyId);
      await refresh();
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <header className="team-header">
        <div>
          <h1 className="h3 mb-1">Company settings</h1>
          <p className="text-secondary mb-0">Company identity, billing details and reusable private delivery sites.</p>
        </div>
      </header>
      {error && <div className="alert alert-danger">{error}</div>}
      {notice && <div className="alert alert-success">{notice}</div>}
      <section className="team-panel company-settings-panel">
        <div className="settings-section-heading">
          <div>
            <h2>Company profile</h2>
            <p>Managers can view this profile. Only the company owner can change billing and bank details.</p>
          </div>
        </div>
        <form onSubmit={saveProfile} className="company-settings-form">
          <div className="settings-grid">
            <Field label="Trading name" value={profile.tradingName} set={(v) => field("tradingName", v)} required disabled={!owner} />
            <Field label="Legal name" value={profile.legalName} set={(v) => field("legalName", v)} required disabled={!owner} />
            <label>
              <span>Business type</span>
              <select className="form-select" value={profile.businessType} disabled={!owner} onChange={(e) => field("businessType", e.target.value as CompanyProfileInput["businessType"])}>
                <option value="company">Limited company</option>
                <option value="sole_trader">Sole trader</option>
                <option value="partnership">Partnership</option>
                <option value="other">Other</option>
              </select>
            </label>
            <Field label="Operational contact" value={profile.operationalContactName ?? ""} set={(v) => field("operationalContactName", v)} disabled={!owner} />
            <Field label="Operational email" type="email" value={profile.operationalEmail ?? ""} set={(v) => field("operationalEmail", v)} disabled={!owner} />
            <Field label="Operational telephone" type="tel" value={profile.operationalTelephone} set={(v) => field("operationalTelephone", v)} required disabled={!owner} />
            <label className="wide">
              <span>Registered / business address</span>
              <textarea className="form-control" rows={3} required value={profile.registeredAddress} disabled={!owner} onChange={(e) => field("registeredAddress", e.target.value)} />
            </label>
            <Field label="Postcode" value={profile.postcode} set={(v) => field("postcode", v.toUpperCase())} required disabled={!owner} />
            <Field label="Country" value={profile.country} set={(v) => field("country", v)} required disabled={!owner} />
          </div>
          <div className="settings-subsection">
            <h3>VAT and payment</h3>
            <div className="settings-grid">
              <label className="check-setting">
                <input type="checkbox" checked={profile.vatRegistered} disabled={!owner} onChange={(e) => field("vatRegistered", e.target.checked)} />
                <span>VAT registered</span>
              </label>
              <Field label="VAT number" value={profile.vatNumber ?? ""} set={(v) => field("vatNumber", v.toUpperCase())} disabled={!owner || !profile.vatRegistered} />
              <label>
                <span>Payment terms (days)</span>
                <input className="form-control" type="number" min={0} max={365} required value={profile.paymentTermsDays} disabled={!owner} onChange={(e) => field("paymentTermsDays", Number(e.target.value))} />
              </label>
            </div>
          </div>
          {owner && (
            <div className="settings-subsection sensitive-settings">
              <h3>Bank details</h3>
              <p>Encrypted before storage and never returned to managers, drivers, bidders or other companies.</p>
              <div className="settings-grid">
                <Field label="Account name" value={profile.bankDetails?.accountName ?? ""} set={(v) => bank("accountName", v)} />
                <Field label="Sort code" value={profile.bankDetails?.sortCode ?? ""} set={(v) => bank("sortCode", v)} />
                <Field label="Account number" value={profile.bankDetails?.accountNumber ?? ""} set={(v) => bank("accountNumber", v)} />
                <Field label="IBAN" value={profile.bankDetails?.iban ?? ""} set={(v) => bank("iban", v)} />
                <Field label="BIC / SWIFT" value={profile.bankDetails?.bic ?? ""} set={(v) => bank("bic", v)} />
              </div>
            </div>
          )}
          {owner && (
            <button className="btn btn-success" disabled={busy}>
              {busy ? "Saving…" : "Save company settings"}
            </button>
          )}
        </form>
        <div className="settings-subsection logo-settings">
          <h3>Company logo</h3>
          <div>
            {logoUrl && <img src={logoUrl} alt="Current company logo" />}
            <div>
              <strong>{logoName ?? "No logo uploaded"}</strong>
              <small>JPG or PNG, maximum 5 MB.</small>
            </div>
          </div>
          <div>
            <label className="btn btn-sm btn-outline-light">
              Choose logo
              <input hidden type="file" accept="image/jpeg,image/png" disabled={busy} onChange={(e) => void logo(e.target.files?.[0])} />
            </label>
            {logoName && (
              <button type="button" className="btn btn-sm btn-outline-danger" disabled={busy} onClick={() => void removeLogo()}>
                Remove
              </button>
            )}
          </div>
        </div>
      </section>
      <FleetTrackingPanel companyId={company.companyId} owner={owner} />
      <section className="team-panel company-settings-panel address-book-panel">
        <div className="settings-section-heading">
          <div>
            <h2>Private address book</h2>
            <p>Entries are templates. Copying one into a load creates an independent snapshot; assignment never reveals or links back to this record.</p>
          </div>
          <button type="button" className="btn btn-sm btn-outline-light" onClick={resetAddress}>
            New address
          </button>
        </div>
        <div className="address-book-layout">
          <div className="address-book-list">
            {addresses.map((entry) => (
              <article className={editing === entry.id ? "active" : ""} key={entry.id}>
                <button type="button" onClick={() => editAddress(entry)}>
                  <strong>{entry.siteName}</strong>
                  <span>{entry.postcode}</span>
                  <small>{entry.fullAddress}</small>
                </button>
                <button type="button" className="address-remove" aria-label={`Remove ${entry.siteName}`} onClick={() => void archive(entry)}>
                  ×
                </button>
              </article>
            ))}
            {!addresses.length && <p className="empty-copy">No saved sites yet.</p>}
          </div>
          <form onSubmit={saveAddress} className="address-editor">
            <h3>{editing ? "Edit saved site" : "Add saved site"}</h3>
            <div className="settings-grid">
              <Field label="Site name" value={address.siteName} set={(v) => setAddress((c) => ({ ...c, siteName: v }))} required />
              <Field label="Postcode" value={address.postcode} set={(v) => setAddress((c) => ({ ...c, postcode: v.toUpperCase() }))} required />
              <label className="wide">
                <span>Full address</span>
                <textarea className="form-control" rows={3} required value={address.fullAddress} onChange={(e) => setAddress((c) => ({ ...c, fullAddress: e.target.value }))} />
              </label>
              <Field label="Contact name" value={address.contactName ?? ""} set={(v) => setAddress((c) => ({ ...c, contactName: v }))} />
              <Field label="Contact telephone" type="tel" value={address.contactTelephone ?? ""} set={(v) => setAddress((c) => ({ ...c, contactTelephone: v }))} />
              <Field label="Contact email" type="email" value={address.contactEmail ?? ""} set={(v) => setAddress((c) => ({ ...c, contactEmail: v }))} />
              <label className="wide">
                <span>Access instructions</span>
                <textarea
                  className="form-control"
                  rows={2}
                  value={address.accessInstructions ?? ""}
                  onChange={(e) =>
                    setAddress((c) => ({
                      ...c,
                      accessInstructions: e.target.value,
                    }))
                  }
                />
              </label>
              <label className="wide">
                <span>Opening / access times</span>
                <textarea className="form-control" rows={2} placeholder="e.g. Mon–Fri 07:00–17:00; booking required after 15:00" value={address.openingHours ?? ""} onChange={(e) => setAddress((c) => ({ ...c, openingHours: e.target.value }))} />
              </label>
              <label className="wide">
                <span>Private notes</span>
                <textarea className="form-control" rows={2} value={address.notes ?? ""} onChange={(e) => setAddress((c) => ({ ...c, notes: e.target.value }))} />
              </label>
            </div>
            <div className="address-editor-actions">
              <button className="btn btn-primary" disabled={busy}>
                {busy ? "Saving…" : editing ? "Save changes" : "Add to address book"}
              </button>
              {editing && (
                <button className="btn btn-outline-light" type="button" onClick={resetAddress}>
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>
      </section>
    </>
  );
}
const trackingSetup: Record<
  TrackingProviderKey,
  {
    intro: string;
    prerequisite: string;
    helpUrl: string;
    helpLabel: string;
    fields: Array<{
      name: string;
      label: string;
      kind: "configuration" | "credentials";
      type?: string;
      placeholder?: string;
    }>;
  }
> = {
  loadsub_device: {
    intro: "Share the GPS location of the device the assigned driver is using. No additional tracking system is required.",
    prerequisite: "The driver must install the LoadSub PWA, grant location permission and keep device tracking active for the load. Phone operating systems may pause location updates in the background.",
    helpUrl: "/support",
    helpLabel: "Device GPS guidance",
    fields: [],
  },
  webfleet: {
    intro: "Connect a WEBFLEET.connect account.",
    prerequisite: "WEBFLEET must enable API access for the account. Your normal Webfleet login is not enough on its own.",
    helpUrl: "https://www.webfleet.com/en_gb/webfleet/partners/integration/developer-resources/",
    helpLabel: "Request Webfleet API access",
    fields: [
      { name: "account", label: "Webfleet account", kind: "configuration" },
      { name: "username", label: "Webfleet username", kind: "configuration" },
      {
        name: "password",
        label: "Webfleet password",
        kind: "credentials",
        type: "password",
      },
    ],
  },
  samsara: {
    intro: "Use a Samsara API token with vehicle read access.",
    prerequisite: "In Samsara, create an API token with Read Vehicles and vehicle statistics access.",
    helpUrl: "https://developers.samsara.com/docs/getting-started",
    helpLabel: "How to create a Samsara token",
    fields: [
      {
        name: "apiKey",
        label: "Samsara API token",
        kind: "credentials",
        type: "password",
        placeholder: "Paste token",
      },
    ],
  },
  geotab: {
    intro: "Connect using a dedicated read-only MyGeotab user.",
    prerequisite: "The user needs permission to read devices and current vehicle status in the selected database.",
    helpUrl: "https://developers.geotab.com/myGeotab/guides/gettingStarted/",
    helpLabel: "MyGeotab API setup",
    fields: [
      { name: "database", label: "MyGeotab database", kind: "configuration" },
      { name: "username", label: "MyGeotab username", kind: "configuration" },
      {
        name: "password",
        label: "MyGeotab password",
        kind: "credentials",
        type: "password",
      },
    ],
  },
  quartix: {
    intro: "Connect a Quartix account after Quartix enables API access.",
    prerequisite: "Quartix API access is supplied on request. Ask Quartix to enable third-party API access before connecting.",
    helpUrl: "https://www.quartix.com/en-gb/integrations-and-partnerships/",
    helpLabel: "Ask Quartix for API access",
    fields: [
      {
        name: "apiKey",
        label: "Quartix API token",
        kind: "credentials",
        type: "password",
        placeholder: "Paste token supplied by Quartix",
      },
    ],
  },
  microlise: {
    intro: "Connect using Microlise integration credentials.",
    prerequisite: "Microlise must provision integration access for the account. Ask your Microlise contact for read-only vehicle location API credentials.",
    helpUrl: "https://www.microlise.com/contact-us/",
    helpLabel: "Contact Microlise",
    fields: [
      {
        name: "apiKey",
        label: "Microlise API token",
        kind: "credentials",
        type: "password",
        placeholder: "Paste token supplied by Microlise",
      },
    ],
  },
};
function FleetTrackingPanel({ companyId, owner }: { companyId: string; owner: boolean }) {
  const [data, setData] = useState<TrackingSummary | null>(null),
    [selected, setSelected] = useState<TrackingProviderKey>(),
    [mappingProvider, setMappingProvider] = useState<TrackingProviderKey>(),
    [discovered, setDiscovered] = useState<TrackingDiscoveredVehicle[]>([]),
    [fleet, setFleet] = useState<Vehicle[]>([]),
    [vehicleChoices, setVehicleChoices] = useState<Record<string, string>>({}),
    [accountLabel, setAccountLabel] = useState(""),
    [values, setValues] = useState<Record<string, string>>({}),
    [matching, setMatching] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [notice, setNotice] = useState("");
  const refresh = () => getCompanyTracking(companyId).then(setData);
  useEffect(() => {
    void refresh().catch((e) => setError(message(e)));
  }, [companyId]);
  function selectProvider(key: TrackingProviderKey, connection?: CompanyTrackingConnection) {
    setSelected(key);
    setAccountLabel(connection?.accountLabel ?? "");
    setValues(connection?.configuration ?? {});
    setMatching(connection?.locationMatchingConsent ?? false);
    setError("");
    setNotice("");
  }
  async function save(event: FormEvent) {
    event.preventDefault();
    if (!selected) return;
    const setup = trackingSetup[selected],
      connection = data?.connections.find((x) => x.providerKey === selected),
      configuration: Record<string, string> = { matchingRadiusMiles: "25" },
      credentials: Record<string, string> = {};
    for (const field of setup.fields) {
      const value = values[field.name]?.trim();
      if (value) (field.kind === "configuration" ? configuration : credentials)[field.name] = value;
    }
    setBusy(true);
    setError("");
    try {
      await saveCompanyTracking(companyId, selected, {
        accountLabel,
        configuration,
        ...(Object.keys(credentials).length ? { credentials } : {}),
        locationMatchingConsent: matching,
      });
      const [remote, local] = await Promise.all([discoverTrackingVehicles(companyId, selected), getFleet(companyId)]),
        powered = local.vehicles.filter((x) => x.assetType === "powered");
      setDiscovered(remote);
      setFleet(powered);
      setVehicleChoices(Object.fromEntries(remote.map((x) => [x.id, x.mappedVehicleId ?? powered.find((v) => normal(v.registration) === normal(x.registration ?? x.label))?.id ?? ""])));
      setValues({});
      setMappingProvider(selected);
      setSelected(undefined);
      await refresh();
      setNotice(`${trackingSetup[selected].intro} Connection verified; match the imported vehicles below.`);
    } catch (e) {
      setError(message(e) + (connection?.status === "connected" ? " Your existing working credentials have not been removed." : ""));
    } finally {
      setBusy(false);
    }
  }
  async function disconnect(key: TrackingProviderKey) {
    if (!window.confirm("Disconnect this tracking provider? Vehicle mappings and location matching will be paused.")) return;
    setBusy(true);
    try {
      await disconnectCompanyTracking(companyId, key);
      await refresh();
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }
  async function registerInterest(key: TrackingProviderKey) {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await registerTrackingProviderInterest(companyId, key);
      await refresh();
      setNotice(`Interest registered. We’ll notify your company when ${data?.providers.find((x) => x.key === key)?.displayName ?? key} is ready.`);
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }
  async function openMapping(key: TrackingProviderKey) {
    setBusy(true);
    setError("");
    try {
      const [remote, local] = await Promise.all([discoverTrackingVehicles(companyId, key), getFleet(companyId)]);
      const powered = local.vehicles.filter((x) => x.assetType === "powered");
      setDiscovered(remote);
      setFleet(powered);
      setVehicleChoices(Object.fromEntries(remote.map((x) => [x.id, x.mappedVehicleId ?? powered.find((v) => normal(v.registration) === normal(x.registration ?? x.label))?.id ?? ""])));
      setMappingProvider(key);
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }
  async function saveMappings() {
    if (!mappingProvider) return;
    setBusy(true);
    setError("");
    try {
      for (const remote of discovered) {
        const vehicleId = vehicleChoices[remote.id];
        if (vehicleId)
          await saveTrackingVehicleMapping(companyId, mappingProvider, {
            vehicleId,
            providerVehicleId: remote.id,
            providerVehicleLabel: remote.label,
            enabled: true,
          });
      }
      await refresh();
      setMappingProvider(undefined);
      setNotice("Vehicle mappings saved. Live positions will now be collected automatically.");
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="team-panel company-settings-panel tracking-connect-panel" id="fleet-tracking">
      <div className="settings-section-heading">
        <div>
          <h2>Fleet Tracking</h2>
          <p>One Tracking Connect subscription covers LoadSub Device GPS and supported external telematics for active-load visibility and nearby-load suggestions.</p>
        </div>
        <span className={`status-chip ${data?.entitled ? "status-active" : "status-grace"}`}>{data?.entitled ? "Tracking Connect active" : "Paid add-on"}</span>
      </div>
      {error && <div className="alert alert-danger">{error}</div>}
      {notice && <div className="alert alert-success">{notice}</div>}
      {!data ? (
        <p>Loading tracking options…</p>
      ) : !data.entitled ? (
        <div className="tracking-paywall">
          <div>
            <strong>Tracking Connect</strong>
            <p>£10/month for up to 30 tracked vehicles, £20 for 31–50, or £30 for 51+. Vehicles merely stored in your fleet do not count.</p>
            <p><strong>No tracking system?</strong> LoadSub Device GPS uses the assigned driver's PWA and device location permission, with no additional tracker required.</p>
            <p><strong>Available now:</strong> {data.providers.filter((provider) => provider.enabled).map((provider) => provider.displayName).join(", ") || "No providers yet"}.</p>
            <p><strong>Coming soon:</strong> {data.providers.filter((provider) => !provider.enabled).map((provider) => provider.displayName).join(", ") || "None"}.</p>
            <div className="tracking-interest-actions">
              {data.providers.filter((provider) => !provider.enabled).map((provider) => (
                <button className="btn btn-sm btn-outline-light" type="button" disabled={busy || provider.interestRegistered} onClick={() => void registerInterest(provider.key)} key={provider.key}>
                  {provider.interestRegistered ? `${provider.displayName} interest registered` : `Notify me about ${provider.displayName}`}
                </button>
              ))}
            </div>
          </div>
          <a className="btn btn-primary" href="/company/billing#tracking-connect">
            Add Tracking Connect
          </a>
        </div>
      ) : (
        <>
          <div className="tracking-privacy-note">
            <strong>Your fleet is not exposed.</strong> Exact locations are restricted to authorised load tracking. Nearby-load matching uses location privately and never lets other carriers browse your vehicles.
          </div>
          <div className="tracking-provider-list">
            {data.providers.map((provider) => {
              const connection = data.connections.find((x) => x.providerKey === provider.key),
                connected = Boolean(connection && connection.status !== "disconnected"),
                setup = trackingSetup[provider.key];
              return (
                <article key={provider.key}>
                  <div>
                    <strong>{provider.displayName}</strong>
                    <small>{!provider.enabled ? "Coming soon — not available to connect yet" : connected ? `${connection!.status} · ${connection!.accountLabel ?? "account connected"}` : "Available now"}</small>
                    {connection?.lastError && <small className="text-danger">{trackingError(connection.lastError)}</small>}
                    <small>{setup.intro}</small>
                  </div>
                  <div>
                    {connected && provider.key!=="loadsub_device" && (
                      <button className="btn btn-sm btn-outline-light" disabled={busy} onClick={() => void openMapping(provider.key)}>
                        Map vehicles
                      </button>
                    )}
                    {connected && provider.key!=="loadsub_device" && (
                      <button className="btn btn-sm btn-outline-danger" disabled={!owner || busy} onClick={() => void disconnect(provider.key)}>
                        Disconnect
                      </button>
                    )}
                    {provider.key==="loadsub_device"?<span className="status-chip status-active">Enabled per load by the assigned driver</span>:provider.enabled ? <button className="btn btn-sm btn-outline-light" disabled={!owner} onClick={() => selectProvider(provider.key, connection)}>{connected ? "Edit" : "Connect"}</button> : <button className="btn btn-sm btn-outline-light" disabled={busy || provider.interestRegistered} onClick={() => void registerInterest(provider.key)}>{provider.interestRegistered ? "Interest registered" : "Notify me when live"}</button>}
                  </div>
                </article>
              );
            })}
          </div>
          {selected && owner && (
            <form className="company-settings-form tracking-connection-form" onSubmit={save}>
              <h3>Connect {data.providers.find((x) => x.key === selected)?.displayName}</h3>
              <div className="tracking-privacy-note">
                <strong>Before connecting:</strong> {trackingSetup[selected].prerequisite}{" "}
                <a href={trackingSetup[selected].helpUrl} target="_blank" rel="noreferrer">
                  {trackingSetup[selected].helpLabel}
                </a>
                .
              </div>
              <div className="settings-grid">
                <Field label="Connection name (optional)" value={accountLabel} set={setAccountLabel} />
                {trackingSetup[selected].fields.map((field) => {
                  const connection = data.connections.find((x) => x.providerKey === selected),
                    hint = connection?.credentialHints[field.name];
                  return (
                    <Field
                      key={field.name}
                      label={field.label + (hint ? ` (saved ${hint})` : "")}
                      type={field.type ?? "text"}
                      value={values[field.name] ?? ""}
                      set={(value) =>
                        setValues((current) => ({
                          ...current,
                          [field.name]: value,
                        }))
                      }
                      required={field.kind === "configuration" || !hint}
                    />
                  );
                })}
                <label className="check-setting wide">
                  <input type="checkbox" checked={matching} onChange={(e) => setMatching(e.target.checked)} />
                  <span>
                    <strong>Use vehicle locations to suggest nearby LoadSub work</strong>
                    <small>Location remains private and can be disabled without disconnecting tracking.</small>
                  </span>
                </label>
              </div>
              <div className="address-editor-actions">
                <button className="btn btn-primary" disabled={busy}>
                  {busy ? "Testing connection…" : "Connect and import vehicles"}
                </button>
                <button className="btn btn-outline-light" type="button" onClick={() => setSelected(undefined)}>
                  Cancel
                </button>
              </div>
            </form>
          )}
          {mappingProvider && (
            <div className="tracking-mapping">
              <h3>Match provider vehicles to LoadSub vehicles</h3>
              {!discovered.length ? (
                <p>The connection works, but the provider returned no vehicles. Check that the provider user or token can read the intended fleet.</p>
              ) : (
                discovered.map((remote) => (
                  <label key={remote.id}>
                    <span>
                      {remote.label}
                      {remote.registration && remote.registration !== remote.label ? ` · ${remote.registration}` : ""}
                    </span>
                    <select
                      className="form-select"
                      value={vehicleChoices[remote.id] ?? ""}
                      onChange={(e) =>
                        setVehicleChoices({
                          ...vehicleChoices,
                          [remote.id]: e.target.value,
                        })
                      }
                    >
                      <option value="">Do not track</option>
                      {fleet.map((vehicle) => (
                        <option value={vehicle.id} key={vehicle.id}>
                          {vehicle.registration} · {vehicle.vehicleClass}
                        </option>
                      ))}
                    </select>
                  </label>
                ))
              )}
              <div className="address-editor-actions">
                <button className="btn btn-primary" disabled={busy || !discovered.length} onClick={() => void saveMappings()}>
                  Save mappings
                </button>
                <button className="btn btn-outline-light" onClick={() => setMappingProvider(undefined)}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}
function normal(value: string) {
  return value.toUpperCase().replace(/[^A-Z0-9]/g, "");
}
function trackingError(code: string) {
  return (
    (
      {
        NOT_CONFIGURED: "LoadSub provider setup is incomplete.",
        AUTHENTICATION_FAILED: "Sign-in details rejected.",
        ACCESS_NOT_ENABLED: "API access is not enabled on this provider account.",
        RATE_LIMITED: "Provider request limit reached.",
        PROVIDER_UNAVAILABLE: "Provider temporarily unavailable.",
        PROVIDER_REJECTED: "Connection rejected by provider.",
        INVALID_RESPONSE: "Unexpected provider response.",
      } as Record<string, string>
    )[code] ?? code
  );
}
function Field({ label, value, set, type = "text", required = false, disabled = false }: { label: string; value: string; set: (value: string) => void; type?: string; required?: boolean; disabled?: boolean }) {
  return (
    <label>
      <span>{label}</span>
      <input className="form-control" type={type} required={required} disabled={disabled} value={value} onChange={(e) => set(e.target.value)} />
    </label>
  );
}
function message(error: unknown) {
  return error instanceof ApiError ? error.message : "Unable to reach LoadSub. Please try again.";
}
